import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWeb3React } from '@web3-react/core';
import { useQuests } from '../../../helpers/hooks/useQuests';
import { usePreviewMode } from '../../../helpers/hooks/useCheckPreviewMode';
import { QUEST_STATUSES, QUEST_TYPES, formatPoints } from '../../../helpers/quests';
import { QuestIcon } from '../../quests/QuestIcon';
import { QuestAction } from '../../quests/QuestAction';
import { WeekTimer } from '../../quests/WeekTimer';
import { getQuestColor, getQuestText } from '../../quests/questText';

export const QuestsBlock = () => {
  const { t } = useTranslation();
  const { account } = useWeb3React();
  const { previewUserAddress } = usePreviewMode();
  const { data, isError, pendingAction, checkHold, claim } = useQuests({
    silent: true,
  });

  // Задания личные: в режиме просмотра чужого кабинета блок не показываем
  if (previewUserAddress || !account || (isError && !data)) {
    return null;
  }

  const total = data?.coreTotal ?? data?.quests?.length ?? 0;
  const done = data?.completedCount ?? 0;
  const regularQuests = data?.quests?.filter((q) => !q.isGold) ?? [];
  const goldQuests = data?.quests?.filter((q) => q.isGold) ?? [];

  const renderRow = (quest) => {
    const { title } = getQuestText(t, quest, data.week);
    const isDone = quest.status !== QUEST_STATUSES.ACTIVE;
    const progressMax = quest.stretchTarget || quest.target;
    return (
      <div
        key={quest.id}
        className="flex items-center justify-between space-x-[1.2rem] py-[1.2rem] border-t-[0.1rem] border-solid border-white-50"
      >
        <div className="flex items-center space-x-[1.2rem] min-w-0">
          <QuestIcon
            type={quest.effectiveType}
            color={isDone ? '#51F689' : getQuestColor(quest)}
            className="w-[2rem] h-[2rem] shrink-0"
          />
          <span className="text-[1.4rem] text-white truncate">
            {title}
            {quest.target > 1 && quest.effectiveType !== QUEST_TYPES.COMBO && (
              <span className="text-white-500">
                {' '}
                · {quest.progress}/{progressMax}
              </span>
            )}
          </span>
        </div>
        <div className="flex items-center space-x-[1.2rem] shrink-0">
          <span className={`text-[1.4rem] font-600 ${isDone ? 'text-green' : 'text-white-500'} lg:hidden`}>
            {quest.stretchPoints && !isDone
              ? t('quests.pointsUpTo', {
                  points: formatPoints(quest.stretchPoints),
                })
              : `+${formatPoints(quest.points)}`}
          </span>
          <QuestAction
            size="sm"
            quest={quest}
            data={data}
            pendingAction={pendingAction}
            onClaim={claim}
            onCheckHold={checkHold}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="flex lg:px-[1.6rem] w-full">
      <div className="flex flex-col w-full p-[2.4rem] dashboard-block-styles space-y-[2rem] lg:p-[2rem] lg:space-y-[1.6rem]">
        <div className="flex items-center justify-between space-x-[1.6rem]">
          <div className="flex items-center space-x-[1.6rem]">
            <div className="flex items-center justify-center w-[5.2rem] h-[5.2rem] rounded-[1.6rem] bg-[rgba(152,90,255,0.12)] lg:w-[4.4rem] lg:h-[4.4rem]">
              <QuestIcon type="invite_multi" />
            </div>
            <div className="flex flex-col space-y-[0.4rem]">
              <span className="text-[2.4rem] font-500 text-white leading-none tracking-[-0.48px] lg:text-[1.8rem]">
                {t('quests.weekTitle')}
              </span>
              {data?.week ? (
                <WeekTimer endsAt={data.week.endsAt} className="text-[1.3rem] text-white-500" />
              ) : (
                <span className="text-[1.3rem] text-white-500">{t('quests.blockSubtitle')}</span>
              )}
            </div>
          </div>
          {data && (
            <div className="flex flex-col items-end space-y-[0.4rem]">
              <span className="text-[1.2rem] text-white-500">{t('quests.balance')}</span>
              <span className="text-[2.4rem] font-600 text-white leading-none lg:text-[1.8rem]">
                {formatPoints(data.pointsBalance)} <span className="text-[1.4rem] text-white-500">PP</span>
              </span>
            </div>
          )}
        </div>

        {!data?.week ? (
          <span className="text-[1.4rem] text-white-500">{t('quests.soon')}</span>
        ) : (
          <>
            <div className="flex flex-col space-y-[0.8rem]">
              <div className="flex items-center justify-between text-[1.3rem]">
                <span className="text-white">{t('quests.progress', { done, total })}</span>
                <span className="text-white-500">{t('quests.bonusShort', { bonus: data.fullClearBonus })}</span>
              </div>
              <div className="h-[0.6rem] rounded-[2rem] bg-white-100 overflow-hidden">
                <div
                  className="h-full rounded-[2rem] bg-primary"
                  style={{ width: `${total ? (done / total) * 100 : 0}%` }}
                />
              </div>
            </div>
            <div className="flex flex-col">{regularQuests.map(renderRow)}</div>
            {goldQuests.length > 0 && (
              <div className="flex flex-col">
                <div className="flex items-center justify-between pb-[0.8rem]">
                  <span className="text-[1.6rem] font-600 text-[#FFC24B]">{t('quests.gold.title')}</span>
                  <span className={`text-[1.2rem] ${data.goldUnlocked ? 'text-green' : 'text-white-500'}`}>
                    {data.goldUnlocked
                      ? `🔓 ${t('quests.gold.unlockedShort')}`
                      : `🔒 ${t('quests.gold.lockedShort', { done: data.completedCount, total: data.coreTotal })}`}
                  </span>
                </div>
                {goldQuests.map(renderRow)}
                <div className="flex items-center justify-between pt-[1.2rem] border-t-[0.1rem] border-solid border-white-50 text-[1.3rem]">
                  <span className="text-[#FFC24B]">
                    {t('quests.grand.title', {
                      bonus: formatPoints(data.grandBonus),
                    })}
                  </span>
                  <span className="text-white-500">
                    {data.claimedCount}/{data.quests.length}
                  </span>
                </div>
              </div>
            )}
          </>
        )}

        <Link to="/quests" className="w-full">
          <div className="flex items-center justify-center h-[4.4rem] w-full rounded-[1.2rem] bg-white-50 border-[0.1rem] border-solid border-white-50 text-[1.4rem] font-500 text-white custom-transition hover:bg-white-100">
            {t('quests.allQuests')}
          </div>
        </Link>
      </div>
    </div>
  );
};
